// GDPR Compliance page
import { createElement } from '@/utils/dom'
import { router } from '@/utils/router'
import { Footer } from '@/components/footer'

export class RGPDPage {

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'legal-page' })

    // Navigation
    page.appendChild(this.createNavigation())
    
    // Main content
    page.appendChild(this.createMainContent())
    
    // Footer
    const footer = new Footer()
    page.appendChild(footer.render())

    return page
  }

  private createNavigation(): HTMLElement {
    const nav = createElement('nav', { className: 'nav' })
    
    nav.innerHTML = `
      <div class="container">
        <div class="nav-container">
          <a href="#" class="nav-logo">
            🗺️ Mapas KM
          </a>
          <ul class="nav-menu">
            <li><a href="/" class="nav-link">Início</a></li>
            <li><a href="/pricing" class="nav-link">Preços</a></li>
            <li><a href="/funcionalidades" class="nav-link">Funcionalidades</a></li>
            <li><a href="/register" class="nav-cta">Começar Grátis →</a></li>
          </ul>
        </div>
      </div>
    `

    // Add click handlers
    nav.addEventListener('click', (e) => {
      e.preventDefault()
      const target = e.target as HTMLElement
      
      if (target.tagName === 'A') {
        const href = target.getAttribute('href')
        if (href && href !== '#') {
          router.navigate(href)
        }
      }
    })

    return nav
  }

  private createMainContent(): HTMLElement {
    const main = createElement('main', { className: 'legal-content' })
    
    main.innerHTML = `
      <div class="container">
        <div class="legal-header">
          <h1>Conformidade RGPD</h1>
          <p class="legal-subtitle">Última atualização: 15 de agosto de 2025</p>
        </div>

        <div class="legal-body">
          <section class="legal-section">
            <h2>1. Introdução</h2>
            <p>Este documento detalha como o Mapas KM cumpre o Regulamento Geral sobre a Proteção de Dados (RGPD - Regulamento UE 2016/679) no processamento de dados pessoais de empresas portuguesas.</p>
          </section>

          <section class="legal-section">
            <h2>2. Responsável pelo Tratamento</h2>
            
            <h3>2.1 Identificação</h3>
            <p><strong>Nome:</strong> ANTONIO LOZADA LDA<br>
            <strong>NIF:</strong> 517712261<br>
            <strong>Morada:</strong> RUA DO ALECRIM 21 6 ESQ<br>
            <strong>Email:</strong> antoniolozadalda@gmail.com</p>

            <h3>2.2 Delegado de Proteção de Dados</h3>
            <p><strong>Email:</strong> antoniolozadalda@gmail.com<br>
            <strong>Responsabilidades:</strong> Supervisão da conformidade RGPD e ponto de contacto com a CNPD</p>
          </section>

          <section class="legal-section">
            <h2>3. Princípios do Tratamento de Dados</h2>
            
            <h3>3.1 Licitude, Lealdade e Transparência (Artigo 5.º(1)(a))</h3>
            <ul>
              <li><strong>Licitude</strong>: Bases legais claramente identificadas para cada tratamento</li>
              <li><strong>Lealdade</strong>: Dados processados de forma consistente com as expectativas</li>
              <li><strong>Transparência</strong>: Informação clara sobre como processamos dados</li>
            </ul>

            <h3>3.2 Limitação das Finalidades (Artigo 5.º(1)(b))</h3>
            <p>Dados recolhidos apenas para finalidades específicas:</p>
            <ul>
              <li>Prestação do serviço de geração de rotas</li>
              <li>Gestão de conta e faturação</li>
              <li>Suporte técnico</li>
              <li>Cumprimento de obrigações legais</li>
            </ul>

            <h3>3.3 Minimização dos Dados (Artigo 5.º(1)(c))</h3>
            <ul>
              <li>Recolhemos apenas dados necessários ao serviço</li>
              <li>Campos opcionais claramente identificados</li>
              <li>Eliminação automática de dados desnecessários</li>
            </ul>
          </section>

          <section class="legal-section">
            <h2>4. Bases Legais do Tratamento</h2>
            
            <div class="legal-grid">
              <div class="legal-card">
                <h4>Consentimento</h4>
                <p>Artigo 6.º(1)(a) RGPD</p>
                <ul>
                  <li>Newsletter e marketing</li>
                  <li>Cookies não essenciais</li>
                  <li>Funcionalidades opcionais</li>
                </ul>
              </div>

              <div class="legal-card">
                <h4>Execução de Contrato</h4>
                <p>Artigo 6.º(1)(b) RGPD</p>
                <ul>
                  <li>Prestação do serviço</li>
                  <li>Processamento de pagamentos</li>
                  <li>Suporte técnico</li>
                </ul>
              </div>

              <div class="legal-card">
                <h4>Interesse Legítimo</h4>
                <p>Artigo 6.º(1)(f) RGPD</p>
                <ul>
                  <li>Segurança e prevenção fraude</li>
                  <li>Melhoria do serviço</li>
                  <li>Análises anonimizadas</li>
                </ul>
              </div>

              <div class="legal-card">
                <h4>Obrigação Legal</h4>
                <p>Artigo 6.º(1)(c) RGPD</p>
                <ul>
                  <li>Registos fiscais</li>
                  <li>Legislação portuguesa</li>
                </ul>
              </div>
            </div>
          </section>

          <section class="legal-section">
            <h2>5. Os Seus Direitos RGPD</h2>
            
            <div class="rights-grid">
              <div class="right-card">
                <h4>🔍 Direito de Acesso</h4>
                <p>Solicite uma cópia dos seus dados</p>
                <span class="right-article">Artigo 15.º</span>
              </div>

              <div class="right-card">
                <h4>✏️ Direito de Retificação</h4>
                <p>Corrija dados imprecisos</p>
                <span class="right-article">Artigo 16.º</span>
              </div>

              <div class="right-card">
                <h4>🗑️ Direito de Apagamento</h4>
                <p>Elimine os seus dados</p>
                <span class="right-article">Artigo 17.º</span>
              </div>

              <div class="right-card">
                <h4>⏸️ Direito de Limitação</h4>
                <p>Limite o processamento</p>
                <span class="right-article">Artigo 18.º</span>
              </div>

              <div class="right-card">
                <h4>📦 Direito de Portabilidade</h4>
                <p>Exporte os seus dados</p>
                <span class="right-article">Artigo 20.º</span>
              </div>

              <div class="right-card">
                <h4>🚫 Direito de Oposição</h4>
                <p>Oponha-se ao processamento</p>
                <span class="right-article">Artigo 21.º</span>
              </div>
            </div>

            <div class="exercise-rights">
              <h3>Como Exercer os Seus Direitos</h3>
              <p>Para exercer qualquer direito RGPD:</p>
              <div class="contact-box">
                <p><strong>📧 Email:</strong> antoniolozadalda@gmail.com</p>
                <p><strong>⏱️ Prazo de resposta:</strong> 30 dias</p>
                <p><strong>📋 Formato:</strong> Dados fornecidos em JSON/CSV</p>
              </div>
            </div>
          </section>

          <section class="legal-section">
            <h2>6. Segurança e Proteção</h2>
            
            <h3>6.1 Medidas de Segurança Implementadas</h3>
            <div class="security-grid">
              <div class="security-item">
                <h4>🔐 Cifragem</h4>
                <p>TLS 1.3 em trânsito<br>AES-256 em repouso</p>
              </div>
              <div class="security-item">
                <h4>🛡️ Autenticação</h4>
                <p>JWT seguros<br>Passwords com Argon2</p>
              </div>
              <div class="security-item">
                <h4>📊 Monitorização</h4>
                <p>Logs detalhados<br>Alertas automáticos</p>
              </div>
              <div class="security-item">
                <h4>💾 Backups</h4>
                <p>Automáticos diários<br>Cifrados e seguros</p>
              </div>
            </div>
          </section>

          <section class="legal-section">
            <h2>7. Contacto e Reclamações</h2>
            
            <h3>7.1 Contacto Direto</h3>
            <div class="contact-box">
              <p><strong>Email prioritário:</strong> antoniolozadalda@gmail.com</p>
              <p><strong>Tempo de resposta:</strong> 48 horas para reconhecimento, 30 dias para resolução</p>
            </div>

            <h3>7.2 Autoridade de Supervisão</h3>
            <div class="authority-box">
              <h4>Comissão Nacional de Proteção de Dados (CNPD)</h4>
              <p>Rua de São Bento, 148-3º<br>
              1200-821 Lisboa<br>
              <strong>Telefone:</strong> 213 928 400<br>
              <strong>Email:</strong> geral@cnpd.pt<br>
              <strong>Website:</strong> www.cnpd.pt</p>
            </div>
          </section>

          <div class="legal-footer">
            <p><strong>Declaração de Conformidade:</strong> O Mapas KM compromete-se a manter total conformidade com o RGPD e a cooperar plenamente com as autoridades de proteção de dados portuguesas e europeias.</p>
          </div>
        </div>
      </div>
    `

    return main
  }
}
